import React, { useEffect, useRef, useState } from 'react';
import ConfirmAction from '@/components/confirmaction';
import ActionMenu from '@/components/actionmenu';
import { Alert, Avatar, Button, Drawer, Empty, Form, Input, Modal, Pagination, Select, Skeleton, Space, message } from 'antd';
import { FilterOutlined, UserOutlined } from '@ant-design/icons';
import PageTopbar from '@/components/pagetopbar';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchMembers, addMembers, removeMember, renameConversation, transferConversationOwner, clearConversation, hideConversation, leaveConversation, dissolveConversation, blockChatUser } from '@/apis/chat';
import { refreshChatSummary } from '@/store/modules/notifications';
import ChatUserPicker from '@/components/chatuserpicker';
import usePrivateList from '@/utils/usePrivateList';
import usePrivateAction from '@/utils/usePrivateAction';
import { compactQueryParams, resourceIdRule } from '@/utils/queryParams';
import useMobile from '@/utils/useMobile';
import MemberDirectory from './MemberDirectory';
import './MembersModal.scss';

const loadMembers = async ({ room_id, caller_id, ...params }) => {
  if (!room_id) return null;
  // 本人角色单独读取，不以筛选后的一页成员推断群主权限或群总人数。
  const [list, self] = await Promise.all([fetchMembers(room_id, params), fetchMembers(room_id, { user_id: caller_id })]);
  if (!Array.isArray(self)) throw new Error('成员列表响应无效');
  if (params.paginate === false) {
    if (!Array.isArray(list)) throw new Error('成员列表响应无效');
    return { data: list, pagination: { total: list.length }, self: self[0] };
  }
  if (!Array.isArray(list?.data) || !list.pagination) throw new Error('成员列表响应无效');
  return { ...list, self: self[0] };
};

const MembersModal = ({ room, userId, open, onClose, onBack, onChanged, hideConversationActions = false, onRemoved, initialInvite = false }) => {
  const [ids, setIds] = useState([]); const [title, setTitle] = useState('');
  const [page, setPage] = useState(1); const [filters, setFilters] = useState({});
  const [form] = Form.useForm();
  const isMobile = useMobile();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const inviteSection = useRef(null);
  const inviteScrolled = useRef(false);
  const roomId = room?.id; const navigate = useNavigate(); const dispatch = useDispatch();
  const { data, loading, error, load, sessionKey } = usePrivateList(loadMembers,
    { room_id: open ? roomId : null, caller_id: userId, ...filters, ...(isMobile ? { paginate: false } : { paginate: true, page, pageSize: 10 }) }, '成员加载失败，请重试');
  const { busy, run } = usePrivateAction();
  useEffect(() => { setTitle(room?.title || ''); setIds([]); }, [roomId, room?.title, open]);
  useEffect(() => { if (open) { setPage(1); setFilters({}); form.resetFields(); setAdvancedOpen(false); setInviteOpen(initialInvite); } }, [roomId, form, open, initialInvite]);
  const members = data?.data || [];
  const allMembers = room?.members || [];
  const groupOwner = !error && room?.kind === 'group' && data?.self?.role === 'owner';
  const ordinaryGroup = room?.kind === 'group' && !room.post;
  const owner = groupOwner && ordinaryGroup;
  const other = room?.kind === 'private' ? allMembers.find(member => String(member.user.id) !== String(userId))?.user : null;
  useEffect(() => {
    if (!open || !inviteOpen) { inviteScrolled.current = false; return; }
    if (isMobile && owner && !loading && !error && !inviteScrolled.current) {
      inviteScrolled.current = true;
      inviteSection.current?.scrollIntoView({ block: 'start' });
    }
  }, [isMobile, open, inviteOpen, owner, loading, error]);
  const act = (operation, text = '会话已更新', exit = false) => run(operation, () => {
    message.success(text); setIds([]);
    dispatch(refreshChatSummary()).catch(() => {});
    if (exit) { onRemoved?.(roomId); onClose(); navigate('/message'); }
    else { void load().catch(() => {}); onChanged(); }
  });
  const renderMember = member => <div className="chat-member-row" key={member.user.id}>
    <Avatar size={isMobile ? 44 : 32} src={member.user.avatar || undefined} icon={<UserOutlined />} />
    <div><strong>{member.user.nickname || member.user.username || '已注销用户'}</strong><span>UID {member.user.id} {member.role === 'owner' && '· 群主'}</span></div>
    {owner && String(member.user.id) !== String(userId) && <Space wrap>
      <ConfirmAction title="确认将群主转交给这位成员？" onConfirm={() => act(() => transferConversationOwner(roomId, member.user.id), '群主已转交')} disabled={busy}><Button size="small" disabled={busy}>转交群主</Button></ConfirmAction>
      <ConfirmAction title="确认移除该成员？" onConfirm={() => act(() => removeMember(roomId, member.user.id))} disabled={busy}><Button size="small" danger disabled={busy}>移除</Button></ConfirmAction>
    </Space>}
  </div>;
  const content = <>
    {!isMobile && room?.post && <Alert type="info" showIcon title="帖子群聊成员随招募审批结果自动调整。" />}
    <div className="message-members-modal-filters" key={sessionKey}>
      <Form form={form} layout={isMobile ? 'vertical' : 'inline'} disabled={busy} onFinish={values => {
        setFilters(compactQueryParams(values));
        setPage(1);
      }}>
        {isMobile ? <div className="message-members-mobile-search">
          <Form.Item name="q"><Input.Search aria-label="成员关键词" maxLength={100} allowClear placeholder={room?.kind === 'group' ? '搜索群成员' : '搜索成员'} onSearch={(q, event) => {
            event?.preventDefault();
            if (busy) return;
            setFilters(current => compactQueryParams({ ...current, q })); setPage(1);
          }} /></Form.Item>
          <Button type="text" icon={<FilterOutlined />} aria-label="成员筛选" aria-expanded={advancedOpen} disabled={busy}
            className={filters.user_id || filters.role ? 'is-selected' : undefined} onClick={() => setAdvancedOpen(value => !value)} />
        </div> : <Form.Item name="q"><Input aria-label="成员关键词" maxLength={100} allowClear placeholder="用户名或昵称" /></Form.Item>}
        <div className="message-members-filter-fields" hidden={isMobile && !advancedOpen}>
        <Form.Item name="user_id" rules={[resourceIdRule('成员 UID')]}><Input aria-label="成员 UID" maxLength={19} allowClear placeholder="成员 UID" /></Form.Item>
        <Form.Item name="role"><Select aria-label="成员角色" allowClear placeholder="全部角色" options={[{ value: 'owner', label: '群主' }, { value: 'member', label: '成员' }]} /></Form.Item>
        <Form.Item><Button htmlType="submit" disabled={busy || loading}>{isMobile ? '应用筛选' : '筛选'}</Button></Form.Item>
        <Form.Item><Button disabled={busy || loading} onClick={() => { form.resetFields(); setFilters({}); setPage(1); }}>重置</Button></Form.Item>
        </div>
      </Form>
    </div>
    {loading ? <Skeleton active /> : error ? <Alert type="error" title={error} action={<Button onClick={() => load().catch(() => {})}>重试</Button>} /> : <>
      <p className="message-members-count">{isMobile ? (Object.keys(filters).length ? '找到' : '共') : '匹配成员'} {data?.pagination.total || 0} {isMobile ? '位成员' : '人'}</p>
      {!members.length && <Empty description="暂无符合条件的成员" />}
      {isMobile ? <MemberDirectory members={members} group={room?.kind === 'group'} renderMember={renderMember} /> : <div className="chat-member-list">{members.map(renderMember)}</div>}
      {!isMobile && data?.pagination.total > 10 && <Pagination current={page} pageSize={10} total={data.pagination.total} disabled={busy} showSizeChanger={false} onChange={setPage} />}
    </>}
    {owner && !loading && !error && <section className="message-members-management">
      {isMobile && <Button type="text" block disabled={busy} onClick={() => setInviteOpen(value => !value)}>邀请成员与群聊设置</Button>}
      <div ref={inviteSection} hidden={isMobile && !inviteOpen}>
      <div className="chat-member-add"><ChatUserPicker value={ids} onChange={setIds} multiple maxCount={Math.max(0, 100 - allMembers.length)} excludeIds={allMembers.map(item => item.user.id)} disabled={busy} /><Button type="primary" disabled={busy || !ids.length} loading={busy} onClick={() => act(() => addMembers(roomId, ids))}>添加</Button></div>
      <div className="chat-member-add"><Input aria-label="群聊名称" maxLength={100} value={title} disabled={busy} onChange={e => setTitle(e.target.value)} /><Button disabled={busy || !title.trim() || title === room.title} onClick={() => act(() => renameConversation(roomId, title.trim()), '群聊名称已更新')}>修改名称</Button></div>
      </div>
    </section>}
    <Space wrap style={{ marginTop: 24 }}>
      <ActionMenu key={`records:${sessionKey}:${roomId}`} label="聊天记录" disabled={busy} items={!open || hideConversationActions ? [] : [
        { key: 'clear', label: '清空记录', confirm: '清空自己可见的聊天记录？无法恢复，其他成员的记录不受影响。', onClick: () => act(() => clearConversation(roomId), '聊天记录已清空') },
        { key: 'hide', label: '隐藏会话', confirm: '隐藏此会话并清空自己的记录？不会退出群聊。', onClick: () => act(() => hideConversation(roomId), '会话已隐藏', true) },
      ]} />
      <ActionMenu key={`room:${sessionKey}:${roomId}`} label="会话管理" disabled={busy} items={!open ? [] : [
        other && { key: 'block', label: '屏蔽用户', danger: true, confirm: '确认屏蔽此用户？双方将无法发起或发送私聊，共同群聊不受影响。', onClick: () => act(() => blockChatUser(other.id), '用户已屏蔽') },
        ordinaryGroup && data?.self && !groupOwner && !loading && !error && { key: 'leave', label: '退出群聊', danger: true, confirm: '确认退出群聊？', onClick: () => act(() => leaveConversation(roomId), '已退出群聊', true) },
        !hideConversationActions && groupOwner && { key: 'dissolve', label: '解散群聊', danger: true, confirm: '确认解散群聊？所有成员将无法继续访问，无法恢复。', onClick: () => act(() => dissolveConversation(roomId), '群聊已解散', true) },
      ]} />
    </Space>
  </>;
  if (isMobile) return <Drawer className="message-members-mobile" placement="right" size="100%" open={open}
    closable={false} keyboard={!busy} mask={{ closable: !busy }} onClose={() => !busy && (onBack || onClose)()}
    title={<PageTopbar title={`${room?.kind === 'group' ? '群聊成员' : '聊天成员'}（${allMembers.length}）`} backLabel="返回聊天信息" disabled={busy} onBack={onBack || onClose} />}>
    {content}
  </Drawer>;
  return <Modal className="message-members-modal" title="会话成员与设置" open={open} onCancel={() => !busy && onClose()} closable={!busy} mask={{ closable: !busy }} keyboard={!busy} footer={null}>{content}</Modal>;
};
export default MembersModal;
